import React, {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  Grid,
  HStack,
  Input,
  Menu,
  Portal,
  Stack,
  Table,
  Text,
} from '@chakra-ui/react';
import { FiChevronDown } from 'react-icons/fi';
import { toasterApi } from '@ui';
import { useSelectedReleaseContext } from '@/hooks/useSelectedReleaseContext';
import {
  useGetProcessesQuery,
  useGetReleaseSettingsQuery,
  useGetWorkOrderCandidatesQuery,
  usePatchReleaseSettingsMutation,
  usePostWorkOrdersMutation,
} from '@/state/api/fabricationApi';
import { useGetAllUserNamesQuery } from '@/state/api/usersApi';
import { FabricationType } from '@/types/fabricationType';
import PartMasterfilePreview from '../../FabricationProductionSummary/components/PartMasterfilePreview';

const PAGE_SIZE = 30;

const CONTROL_PROPS = {
  bg: 'bg',
  borderColor: 'border',
  color: 'fg',
};

const CHECKBOX_CONTROL_PROPS = {
  bg: 'bg',
  borderColor: 'border',
  _checked: { bg: 'blue.500', borderColor: 'blue.500', color: 'white' },
};

const DROPDOWN_TRIGGER_PROPS = {
  ...CONTROL_PROPS,
  w: 'full',
  justifyContent: 'space-between',
  fontWeight: 'normal',
  borderWidth: '1px',
  borderRadius: 'md',
  px: 3,
  minH: '32px',
  overflow: 'hidden',
};

const DROPDOWN_CONTENT_PROPS = {
  bg: 'bg.panel',
  borderColor: 'border',
  color: 'fg',
  borderWidth: '1px',
  borderRadius: 'md',
  boxShadow: 'lg',
  p: 1,
  maxH: '280px',
  overflowY: 'auto' as const,
  minW: '220px',
  zIndex: 2000,
};

const TABLE_CSS = {
  '& tbody tr:nth-of-type(odd)': {
    background: 'var(--chakra-colors-bg-subtle)',
  },
  '& tbody tr:nth-of-type(even)': {
    background: 'var(--chakra-colors-bg-muted)',
  },
  '& td, & th': {
    borderColor: 'var(--chakra-colors-border-muted)',
    verticalAlign: 'middle',
    color: 'var(--chakra-colors-fg)',
  },
};

type StepDraft = {
  process_step_id: number;
  user_ids: number[];
};

type RowDraft = {
  selected: boolean;
  process_id: number | '';
  qty_planned: number;
  qty_planned_input: string;
  due_date: string;
  description: string;
  stepAssignments: StepDraft[];
};

type SelectOption = {
  value: string;
  label: string;
};

const useDebouncedValue = <T,>(value: T, delay = 150) => {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timeout);
  }, [value, delay]);

  return debounced;
};

const buildInitialDraft = (
  item: FabricationType.WorkOrderCandidate,
  defaultDueDate = '',
): RowDraft => ({
  selected: false,
  process_id: '',
  qty_planned: item.remaining_qty,
  qty_planned_input: String(item.remaining_qty),
  due_date: defaultDueDate,
  description: '',
  stepAssignments: [],
});

type SingleSelectDropdownProps = {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder: string;
  minW?: string;
};

const SingleSelectDropdown = ({
  value,
  onChange,
  options,
  placeholder,
  minW = '180px',
}: SingleSelectDropdownProps) => {
  const selected = options.find((option) => option.value === value);

  return (
    <Menu.Root positioning={{ placement: 'bottom-start', sameWidth: true }}>
      <Menu.Trigger asChild>
        <Button size="sm" variant="outline" {...DROPDOWN_TRIGGER_PROPS} minW={minW}>
          <HStack w="full" justify="space-between" gap={2}>
            <Text
              flex="1"
              textAlign="left"
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
              color={selected ? 'fg' : 'fg.muted'}
            >
              {selected?.label ?? placeholder}
            </Text>
            <FiChevronDown color="currentColor" size={16} />
          </HStack>
        </Button>
      </Menu.Trigger>

      <Portal>
        <Menu.Positioner>
          <Menu.Content {...DROPDOWN_CONTENT_PROPS}>
            {options.map((option) => (
              <Menu.Item
                key={option.value}
                value={option.value}
                onClick={() => onChange(option.value)}
                bg={value === option.value ? 'bg.subtle' : 'transparent'}
                _hover={{ bg: 'bg.subtle' }}
                _focus={{ bg: 'bg.subtle' }}
              >
                <HStack justify="space-between" w="full">
                  <Text color="fg">{option.label}</Text>
                  {value === option.value ? <Text color="green.500">✓</Text> : null}
                </HStack>
              </Menu.Item>
            ))}
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  );
};

type MultiSelectDropdownProps = {
  value: string[];
  onChange: (value: string[]) => void;
  options: SelectOption[];
  placeholder: string;
  minW?: string;
};

const MultiSelectDropdown = ({
  value,
  onChange,
  options,
  placeholder,
  minW = '240px',
}: MultiSelectDropdownProps) => {
  const selectedLabels = options
    .filter((option) => value.includes(option.value))
    .map((option) => option.label);

  const displayLabel =
    selectedLabels.length === 0
      ? placeholder
      : selectedLabels.length <= 2
        ? selectedLabels.join(', ')
        : `${selectedLabels.length} selected`;

  const toggleValue = (nextValue: string) => {
    if (value.includes(nextValue)) {
      onChange(value.filter((v) => v !== nextValue));
      return;
    }
    onChange([...value, nextValue]);
  };

  return (
    <Menu.Root closeOnSelect={false} positioning={{ placement: 'bottom-start' }}>
      <Menu.Trigger asChild>
        <Button size="sm" variant="outline" {...DROPDOWN_TRIGGER_PROPS} minW={minW}>
          <HStack w="full" justify="space-between" gap={2}>
            <Text
              flex="1"
              textAlign="left"
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
              color={selectedLabels.length ? 'fg' : 'fg.muted'}
            >
              {displayLabel}
            </Text>
            <FiChevronDown color="currentColor" size={16} />
          </HStack>
        </Button>
      </Menu.Trigger>

      <Portal>
        <Menu.Positioner>
          <Menu.Content {...DROPDOWN_CONTENT_PROPS}>
            {options.length === 0 ? (
              <Box px={3} py={2}>
                <Text color="fg.muted" fontSize="sm">
                  No options
                </Text>
              </Box>
            ) : null}

            {options.map((option) => {
              const checked = value.includes(option.value);

              return (
                <Menu.Item
                  key={option.value}
                  value={option.value}
                  onClick={() => toggleValue(option.value)}
                  _hover={{ bg: 'bg.subtle' }}
                  _focus={{ bg: 'bg.subtle' }}
                >
                  <HStack w="full" justify="space-between">
                    <Checkbox.Root checked={checked} pointerEvents="none">
                      <Checkbox.HiddenInput />
                      <Checkbox.Control {...CHECKBOX_CONTROL_PROPS}>
                        <Checkbox.Indicator />
                      </Checkbox.Control>
                      <Checkbox.Label color="fg">{option.label}</Checkbox.Label>
                    </Checkbox.Root>
                  </HStack>
                </Menu.Item>
              );
            })}
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  );
};

type CandidateRowProps = {
  item: FabricationType.WorkOrderCandidate;
  processes: FabricationType.Process[];
  users: { id: number; display_name: string }[];
  expanded: boolean;
  isPreviewSelected: boolean;
  onToggleExpanded: (partId: number) => void;
  onDraftChange: (partId: number, draft: RowDraft) => void;
  onSelectPreview: (partNumber: string) => void;
  initialDraft: RowDraft;
};

const CandidateRow = memo(
  ({
    item,
    processes,
    users,
    expanded,
    isPreviewSelected,
    onToggleExpanded,
    onDraftChange,
    onSelectPreview,
    initialDraft,
  }: CandidateRowProps) => {
    const [draft, setDraft] = useState<RowDraft>(initialDraft);

    useEffect(() => {
      setDraft(initialDraft);
    }, [initialDraft]);

    const selectedProcess = useMemo(
      () =>
        draft.process_id
          ? processes.find((process) => process.id === draft.process_id) ?? null
          : null,
      [draft.process_id, processes],
    );

    const processOptions = useMemo<SelectOption[]>(
      () =>
        processes.map((process) => ({
          value: String(process.id),
          label: process.name,
        })),
      [processes],
    );

    const userOptions = useMemo<SelectOption[]>(
      () =>
        users.map((user) => ({
          value: String(user.id),
          label: user.display_name,
        })),
      [users],
    );

    const patchDraft = useCallback(
      (updater: (prev: RowDraft) => RowDraft) => {
        setDraft((prev) => {
          const next = updater(prev);
          onDraftChange(item.part_id, next);
          return next;
        });
      },
      [item.part_id, onDraftChange],
    );

    const stopRowClick = useCallback((e: React.MouseEvent) => {
      e.stopPropagation();
    }, []);

    const setProcessForRow = useCallback(
      (processId: number | '') => {
        const process = processId
          ? processes.find((p) => p.id === processId) ?? null
          : null;

        patchDraft((prev) => ({
          ...prev,
          process_id: processId,
          stepAssignments: process
            ? process.steps.map((step) => ({
                process_step_id: step.id,
                user_ids: [],
              }))
            : [],
        }));
      },
      [patchDraft, processes],
    );

    const patchStepAssignment = useCallback(
      (processStepId: number, userIds: number[]) => {
        patchDraft((prev) => ({
          ...prev,
          stepAssignments: prev.stepAssignments.map((assignment) =>
            assignment.process_step_id === processStepId
              ? { ...assignment, user_ids: userIds }
              : assignment,
          ),
        }));
      },
      [patchDraft],
    );

    const handleQtyInputChange = useCallback(
      (value: string) => {
        if (value === '') {
          patchDraft((prev) => ({
            ...prev,
            qty_planned_input: '',
          }));
          return;
        }

        if (!/^\d+$/.test(value)) return;

        patchDraft((prev) => ({
          ...prev,
          qty_planned_input: value,
          qty_planned: Number(value),
        }));
      },
      [patchDraft],
    );

    const handleQtyBlur = useCallback(() => {
      patchDraft((prev) => {
        const parsed = Number(prev.qty_planned_input);
        const normalized = Number.isFinite(parsed)
          ? Math.max(1, Math.min(parsed, item.remaining_qty))
          : 1;

        return {
          ...prev,
          qty_planned: normalized,
          qty_planned_input: String(normalized),
        };
      });
    }, [patchDraft, item.remaining_qty]);

    return (
      <Fragment>
        <Table.Row
          onClick={() => onSelectPreview(item.part_number)}
          cursor="pointer"
          bg={isPreviewSelected ? 'bg.emphasized' : undefined}
          _hover={{
            '& td': {
              bg: isPreviewSelected ? 'bg.emphasized' : 'bg.subtle',
            },
          }}
        >
          <Table.Cell>
            <Box onClick={stopRowClick}>
              <Checkbox.Root
                checked={draft.selected}
                onCheckedChange={(details) =>
                  patchDraft((prev) => ({
                    ...prev,
                    selected: details.checked === true,
                  }))
                }
              >
                <Checkbox.HiddenInput />
                <Checkbox.Control {...CHECKBOX_CONTROL_PROPS}>
                  <Checkbox.Indicator />
                </Checkbox.Control>
              </Checkbox.Root>
            </Box>
          </Table.Cell>

          <Table.Cell minW="260px">
            <Stack gap={1}>
              <Text fontWeight="medium" color="fg">
                {item.part_number}
              </Text>
              <HStack gap={2} wrap="wrap">
                <Badge>{item.role}</Badge>
                <Badge variant="subtle">Remaining {item.remaining_qty}</Badge>
                {item.open_work_order_qty > 0 ? (
                  <Badge variant="subtle">Open WO {item.open_work_order_qty}</Badge>
                ) : null}
                {item.frame_names?.length ? (
                  <Badge variant="subtle">
                    {item.frame_count === 1
                      ? `Frame ${item.frame_names[0]}`
                      : `${item.frame_count} frames`}
                  </Badge>
                ) : null}
                {isPreviewSelected ? (
                  <Badge colorPalette="blue" variant="subtle">
                    Drawing selected
                  </Badge>
                ) : null}
              </HStack>
              {item.data?.description ? (
                <Text fontSize="xs" color="fg.muted" lineClamp={1}>
                  {item.data.description}
                </Text>
              ) : null}
              {item.frame_names?.length ? (
                <Text fontSize="xs" color="fg.subtle" lineClamp={1}>
                  {item.frame_names.join(', ')}
                </Text>
              ) : null}
            </Stack>
          </Table.Cell>

          <Table.Cell textAlign="end" color="fg">
            {item.required_qty}
          </Table.Cell>
          <Table.Cell textAlign="end" color="fg">
            {item.remaining_qty}
          </Table.Cell>

          <Table.Cell minW="180px">
            <Box onClick={stopRowClick}>
              <SingleSelectDropdown
                value={draft.process_id === '' ? '' : String(draft.process_id)}
                onChange={(value) => setProcessForRow(value ? Number(value) : '')}
                options={processOptions}
                placeholder="Select process"
                minW="180px"
              />
            </Box>
          </Table.Cell>

          <Table.Cell minW="110px">
            <Box onClick={stopRowClick}>
              <Input
                size="sm"
                type="number"
                min={1}
                max={item.remaining_qty}
                step={1}
                value={draft.qty_planned_input}
                onChange={(e) => handleQtyInputChange(e.target.value)}
                onBlur={handleQtyBlur}
                {...CONTROL_PROPS}
              />
            </Box>
          </Table.Cell>

          <Table.Cell minW="160px">
            <Box onClick={stopRowClick}>
              <Input
                size="sm"
                type="datetime-local"
                value={draft.due_date}
                onChange={(e) =>
                  patchDraft((prev) => ({
                    ...prev,
                    due_date: e.target.value,
                  }))
                }
                {...CONTROL_PROPS}
              />
            </Box>
          </Table.Cell>

          <Table.Cell minW="220px">
            <Box onClick={stopRowClick}>
              <Input
                size="sm"
                value={draft.description}
                onChange={(e) =>
                  patchDraft((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                {...CONTROL_PROPS}
              />
            </Box>
          </Table.Cell>

          <Table.Cell minW="120px">
            <Box onClick={stopRowClick}>
              <Button
                size="xs"
                variant="outline"
                disabled={!selectedProcess}
                onClick={() => onToggleExpanded(item.part_id)}
              >
                {expanded ? 'Hide steps' : 'Step users'}
              </Button>
            </Box>
          </Table.Cell>
        </Table.Row>

        {expanded && selectedProcess ? (
          <Table.Row>
            <Table.Cell />
            <Table.Cell colSpan={8}>
              <Box
                borderWidth="1px"
                borderRadius="md"
                p={3}
                bg="bg.panel"
                borderColor="border"
                w="full"
                minW={0}
              >
                <Text fontWeight="semibold" mb={3} color="fg">
                  Step user assignment for {selectedProcess.name}
                </Text>

                <Grid
                  templateColumns={{
                    base: '1fr',
                    md: '220px 220px minmax(260px,1fr)',
                  }}
                  gap={3}
                  w="full"
                >
                  {selectedProcess.steps.map((step) => {
                    const assignment =
                      draft.stepAssignments.find((row) => row.process_step_id === step.id) ?? {
                        process_step_id: step.id,
                        user_ids: [],
                      };

                    const machineLabel =
                      step.allowed_machines?.length > 0
                        ? step.allowed_machines[0]?.name
                        : 'No machine mapped';

                    return (
                      <Fragment key={step.id}>
                        <Box minW={0}>
                          <Text fontWeight="medium" color="fg">
                            {step.sequence}. {step.name}
                          </Text>
                          <Text fontSize="xs" color="fg.subtle">
                            {step.code || 'No code'}
                          </Text>
                        </Box>

                        <Box minW={0}>
                          <Text fontSize="sm" color="fg">
                            {machineLabel}
                          </Text>
                        </Box>

                        <Box minW={0}>
                          <Box onClick={stopRowClick}>
                            <MultiSelectDropdown
                              value={assignment.user_ids.map(String)}
                              onChange={(values) => patchStepAssignment(step.id, values.map(Number))}
                              options={userOptions}
                              placeholder="Assign at least one user"
                              minW="260px"
                            />
                          </Box>
                        </Box>
                      </Fragment>
                    );
                  })}
                </Grid>
              </Box>
            </Table.Cell>
          </Table.Row>
        ) : null}
      </Fragment>
    );
  },
);

CandidateRow.displayName = 'CandidateRow';

const toDatetimeLocal = (iso: string | null | undefined): string => {
  if (!iso) return '';
  try {
    // Date-only "YYYY-MM-DD" has no 'T' and is parsed as UTC midnight by
    // the Date constructor, which shifts the displayed day in negative-offset
    // zones. Force local midnight by appending T00:00.
    const d = iso.includes('T') ? new Date(iso) : new Date(`${iso}T00:00`);
    if (isNaN(d.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    return (
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
      `T${pad(d.getHours())}:${pad(d.getMinutes())}`
    );
  } catch {
    return '';
  }
};

const CreateWorkOrders: React.FC = () => {
  const { projectId, releaseId, release } = useSelectedReleaseContext();
  const releaseDueDate = release?.due_date ?? null;
  const {
    data: releaseSettings,
    isFetching: isFetchingSettings,
  } = useGetReleaseSettingsQuery(
    { projectId: projectId as number, releaseId: releaseId as number },
    { skip: !projectId || !releaseId },
  );
  const settingsDueDate = releaseSettings?.default_work_order_due_date ?? null;

  const {
    data: rows = [],
    isLoading: isLoadingRows,
    refetch: refetchCandidates,
  } = useGetWorkOrderCandidatesQuery(
    { projectId: projectId as number, releaseId: releaseId as number },
    { skip: !projectId || !releaseId },
  );

  const { data: processes = [] } = useGetProcessesQuery(
    { projectId: projectId as number, active: true } as any,
    { skip: !projectId },
  );

  const { data: users = [] } = useGetAllUserNamesQuery();
  const [postWorkOrders, { isLoading: isCreating }] = usePostWorkOrdersMutation();
  const [patchReleaseSettings, { isLoading: isSavingSettings }] =
    usePatchReleaseSettingsMutation();

  const [drafts, setDrafts] = useState<Record<number, RowDraft>>({});
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});
  const [search, setSearch] = useState('');
  const [processFilter, setProcessFilter] = useState('');
  const [page, setPage] = useState(1);
  const [previewPartNumber, setPreviewPartNumber] = useState<string>('');
  const [showPartDrawingPreview, setShowPartDrawingPreview] = useState(true);
  const [topDueDate, setTopDueDate] = useState<string>(
    () => toDatetimeLocal(settingsDueDate ?? releaseDueDate),
  );

  // Prefer persisted fabrication default over release due date once settings load.
  useEffect(() => {
    if (isFetchingSettings) return;
    if (settingsDueDate) {
      setTopDueDate(toDatetimeLocal(settingsDueDate));
      return;
    }
    if (releaseDueDate && !topDueDate) {
      setTopDueDate(toDatetimeLocal(releaseDueDate));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsDueDate, releaseDueDate, isFetchingSettings]);

  const debouncedSearch = useDebouncedValue(search, 150);

  const processFilterOptions = useMemo<SelectOption[]>(
    () =>
      processes.map((process) => ({
        value: String(process.id),
        label: process.name,
      })),
    [processes],
  );

  const filteredRows = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();

    return rows.filter((item) => {
      if (q) {
        const haystack = [
          item.part_number,
          item.data?.description,
          item.data?.item,
          item.data?.finish,
          item.role,
          ...(item.frame_names ?? []),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        if (!haystack.includes(q)) return false;
      }

      if (processFilter) {
        const draft = drafts[item.part_id];
        if (!draft || String(draft.process_id) !== processFilter) return false;
      }

      return true;
    });
  }, [rows, debouncedSearch, processFilter, drafts]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);

  const pagedRows = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return filteredRows.slice(start, start + PAGE_SIZE);
  }, [filteredRows, safePage]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, processFilter]);

  useEffect(() => {
    if (!rows.length) {
      setPreviewPartNumber('');
      return;
    }

    const previewStillExists = rows.some((row) => row.part_number === previewPartNumber);
    if (!previewPartNumber || !previewStillExists) {
      setPreviewPartNumber(rows[0].part_number);
    }
  }, [rows, previewPartNumber]);

  useEffect(() => {
    if (!filteredRows.length) {
      setPreviewPartNumber('');
      return;
    }

    const previewStillVisible = filteredRows.some(
      (row) => row.part_number === previewPartNumber,
    );

    if (!previewPartNumber || !previewStillVisible) {
      setPreviewPartNumber(filteredRows[0].part_number);
    }
  }, [filteredRows, previewPartNumber]);

  const getDraft = useCallback(
    (item: FabricationType.WorkOrderCandidate) =>
      drafts[item.part_id] ?? buildInitialDraft(item, topDueDate),
    [drafts, topDueDate],
  );

  const applyTopDueDateToAllRows = useCallback(async () => {
    if (!projectId || !releaseId || !topDueDate) return;

    try {
      await patchReleaseSettings({
        projectId,
        releaseId,
        body: {
          default_work_order_due_date: new Date(topDueDate).toISOString(),
        },
      }).unwrap();

      setDrafts((prev) => {
        const next = { ...prev };
        rows.forEach((item) => {
          const existing = prev[item.part_id] ?? buildInitialDraft(item, topDueDate);
          next[item.part_id] = { ...existing, due_date: topDueDate };
        });
        return next;
      });

      toasterApi({
        type: 'success',
        message: 'Default fabrication date saved.',
        detail: 'The date was applied to current rows and will prefill future work orders for this release.',
      });
    } catch (error: any) {
      toasterApi({
        type: 'error',
        message: 'Could not save default date.',
        detail: error?.data?.detail ?? 'Apply all date was not persisted.',
      });
    }
  }, [projectId, releaseId, topDueDate, patchReleaseSettings, rows]);

  const handleDraftChange = useCallback((partId: number, draft: RowDraft) => {
    setDrafts((prev) => ({
      ...prev,
      [partId]: draft,
    }));
  }, []);

  const toggleExpanded = useCallback((partId: number) => {
    setExpandedRows((prev) => ({
      ...prev,
      [partId]: !prev[partId],
    }));
  }, []);

  const bulkSelectedCount = useMemo(
    () =>
      rows.reduce((count, item) => {
        const draft = drafts[item.part_id];
        return count + (draft?.selected ? 1 : 0);
      }, 0),
    [rows, drafts],
  );

  const createSelected = async () => {
    if (!projectId || !releaseId) return;

    const selectedRows = rows
      .map((item) => ({ item, draft: getDraft(item) }))
      .filter(({ draft }) => draft.selected);

    const invalidStepAssignment = selectedRows.find(({ draft }) => {
      if (!draft.process_id) return false;
      const process = processes.find((p) => p.id === draft.process_id);
      if (!process) return false;

      return process.steps.some((step) => {
        const stepAssignment = draft.stepAssignments.find(
          (assignment) => assignment.process_step_id === step.id,
        );
        return !stepAssignment || stepAssignment.user_ids.length === 0;
      });
    });

    if (invalidStepAssignment) {
      toasterApi({
        type: 'warning',
        message: 'Step assignment required.',
        detail: 'Each process step must have at least one assigned user.',
      });
      return;
    }

    const payload = selectedRows
      .map(({ item, draft }) => ({
        part_id: item.part_id,
        process_id: Number(draft.process_id),
        qty_planned: Number(draft.qty_planned_input || draft.qty_planned),
        description: draft.description || null,
        due_date: draft.due_date
          ? new Date(draft.due_date).toISOString()
          : null,
        step_user_assignments: draft.stepAssignments.map((assignment) => ({
          process_step_id: assignment.process_step_id,
          user_ids: assignment.user_ids,
        })),
      }))
      .filter((row) => row.process_id > 0 && row.qty_planned > 0);

    if (!payload.length) {
      toasterApi({
        type: 'warning',
        message: 'No valid work orders selected.',
        detail: 'Select parts, choose a process, and assign step users.',
      });
      return;
    }

    try {
      const result = await postWorkOrders({
        projectId,
        releaseId,
        body: payload,
      }).unwrap();

      toasterApi({
        type: 'success',
        message: 'Fabrication work orders created.',
        detail: `${result.length} work order(s) were created.`,
      });

      setDrafts({});
      setExpandedRows({});
      await refetchCandidates();
    } catch (error: any) {
      toasterApi({
        type: 'error',
        message: 'Creation failed.',
        detail: error?.data?.detail ?? 'Could not create work orders.',
      });
    }
  };

  const clearFilters = () => {
    setSearch('');
    setProcessFilter('');
  };

  if (!projectId || !releaseId) {
    return <Text color="fg">Select a release first.</Text>;
  }

  return (
    <Stack gap={4} w="full" minW={0}>
      <Box
        w="full"
        minW={0}
        borderWidth="1px"
        borderRadius="md"
        p={{ base: 4, md: 5 }}
        bg="bg.panel"
        borderColor="border"
      >
        <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
          <Stack gap={1} minW={0} flex="1">
            <Text fontWeight="semibold" color="fg">
              Create fabrication work orders
            </Text>
            <Text color="fg.muted" fontSize="sm">
              Create work orders against the remaining quantity for each aggregated part in this release.
            </Text>
            <HStack gap={2} mt={1} wrap="wrap">
              <Badge variant="subtle" colorPalette={releaseDueDate ? 'blue' : 'gray'}>
                Release default:{' '}
                {releaseDueDate
                  ? new Date(releaseDueDate).toLocaleDateString()
                  : 'Not set'}
              </Badge>
              <Badge variant="subtle" colorPalette={settingsDueDate ? 'green' : 'gray'}>
                Fabrication default:{' '}
                {settingsDueDate
                  ? new Date(settingsDueDate).toLocaleString()
                  : 'Not set'}
              </Badge>
              <Text fontSize="xs" color="fg.subtle">
                Optional. Use the date picker in the Due column header to save and fill all rows at once.
              </Text>
            </HStack>
          </Stack>

          <Button
            colorScheme="blue"
            onClick={createSelected}
            loading={isCreating}
            w={{ base: '100%', md: 'auto' }}
          >
            Create selected ({bulkSelectedCount})
          </Button>
        </Flex>
      </Box>

      <Box
        w="full"
        minW={0}
        borderWidth="1px"
        borderRadius="md"
        p={{ base: 4, md: 5 }}
        bg="bg.panel"
        borderColor="border"
      >
        <Flex justify="space-between" align={{ base: 'stretch', md: 'center' }} wrap="wrap" gap={3} mb={4}>
          <Stack gap={1}>
            <Text fontWeight="semibold" color="fg">
              Part drawing preview
            </Text>
            <Text fontSize="sm" color="fg.subtle">
              Click any row to automatically preview that part drawing.
            </Text>
          </Stack>

          <Checkbox.Root
  checked={showPartDrawingPreview}
  onCheckedChange={(details) =>
    setShowPartDrawingPreview(details.checked === true)
  }
>
  <Checkbox.HiddenInput />
  <Checkbox.Control
    bg="bg"
    borderColor="border"
    _checked={{
      bg: 'black',
      borderColor: 'black',
      color: 'white',
    }}
  >
    <Checkbox.Indicator color="white" />
  </Checkbox.Control>
  <Checkbox.Label color="fg">Show preview panel</Checkbox.Label>
</Checkbox.Root>
        </Flex>

        <Grid
          templateColumns={
            showPartDrawingPreview
              ? { base: '1fr', xl: 'minmax(0, 1.4fr) minmax(360px, 0.9fr)' }
              : { base: '1fr' }
          }
          gap={4}
        >
          <Box w="full" minW={0}>
            <Flex gap={3} wrap="wrap" mb={4} w="full">
              <Input
                flex={{ base: '1 1 100%', md: '1 1 320px' }}
                minW={{ base: '100%', md: '260px' }}
                placeholder="Search part, frames, description..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                {...CONTROL_PROPS}
              />

              <Box
                flex={{ base: '1 1 100%', md: '0 1 220px' }}
                minW={{ base: '100%', md: '220px' }}
              >
                <SingleSelectDropdown
                  value={processFilter}
                  onChange={setProcessFilter}
                  options={processFilterOptions}
                  placeholder="All selected processes"
                  minW="220px"
                />
              </Box>

              <Button variant="outline" onClick={clearFilters} w={{ base: '100%', md: 'auto' }}>
                Reset filters
              </Button>
            </Flex>

            {isLoadingRows || isFetchingSettings ? <Text color="fg">Loading available parts…</Text> : null}
            {!isLoadingRows && !filteredRows.length ? (
              <Text color="fg.muted">
                No remaining fabrication quantity is available for work order creation.
              </Text>
            ) : null}

            {!!filteredRows.length ? (
              <>
                <Box w="full" minW={0}>
                  <Table.ScrollArea
                    width="full"
                    overflowX="auto"
                    borderWidth="1px"
                    borderColor="border"
                    borderRadius="md"
                  >
                    <Table.Root
                      size="sm"
                      variant="outline"
                      interactive
                      striped
                      showColumnBorder
                      minW="max-content"
                      width="full"
                      css={TABLE_CSS}
                    >
                      <Table.Header>
                        <Table.Row bg="bg.emphasized">
                          <Table.ColumnHeader color="fg" />
                          <Table.ColumnHeader minW="260px" color="fg">
                            Part
                          </Table.ColumnHeader>
                          <Table.ColumnHeader textAlign="end" minW="110px" color="fg">
                            Required
                          </Table.ColumnHeader>
                          <Table.ColumnHeader textAlign="end" minW="110px" color="fg">
                            Remaining
                          </Table.ColumnHeader>
                          <Table.ColumnHeader minW="180px" color="fg">
                            Process
                          </Table.ColumnHeader>
                          <Table.ColumnHeader textAlign="end" minW="110px" color="fg">
                            WO Qty
                          </Table.ColumnHeader>
                          <Table.ColumnHeader minW="200px" color="fg">
                            <Stack gap={1}>
                              <Text>Due</Text>
                              <HStack gap={1} wrap="wrap">
                                <Input
                                  size="xs"
                                  type="datetime-local"
                                  value={topDueDate}
                                  onChange={(e) => setTopDueDate(e.target.value)}
                                  {...CONTROL_PROPS}
                                  minW="140px"
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <Button
                                  size="xs"
                                  variant="outline"
                                  colorPalette="blue"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    applyTopDueDateToAllRows();
                                  }}
                                  loading={isSavingSettings}
                                  disabled={!topDueDate}
                                >
                                  Apply to all
                                </Button>
                              </HStack>
                            </Stack>
                          </Table.ColumnHeader>
                          <Table.ColumnHeader minW="220px" color="fg">
                            Description
                          </Table.ColumnHeader>
                          <Table.ColumnHeader minW="120px" color="fg">
                            Actions
                          </Table.ColumnHeader>
                        </Table.Row>
                      </Table.Header>

                      <Table.Body>
                        {pagedRows.map((item) => (
                          <CandidateRow
                            key={item.part_id}
                            item={item}
                            processes={processes}
                            users={users}
                            expanded={!!expandedRows[item.part_id]}
                            isPreviewSelected={previewPartNumber === item.part_number}
                            onToggleExpanded={toggleExpanded}
                            onDraftChange={handleDraftChange}
                            onSelectPreview={setPreviewPartNumber}
                            initialDraft={getDraft(item)}
                          />
                        ))}
                      </Table.Body>
                    </Table.Root>
                  </Table.ScrollArea>
                </Box>

                <Flex justify="space-between" align="center" mt={4} wrap="wrap" gap={3}>
                  <Text fontSize="sm" color="fg.subtle">
                    Showing {(safePage - 1) * PAGE_SIZE + 1}–
                    {Math.min(safePage * PAGE_SIZE, filteredRows.length)} of {filteredRows.length}
                  </Text>

                  <HStack flexWrap="wrap">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={safePage <= 1}
                      onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                    >
                      Previous
                    </Button>
                    <Text fontSize="sm" color="fg">
                      Page {safePage} / {totalPages}
                    </Text>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={safePage >= totalPages}
                      onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                    >
                      Next
                    </Button>
                  </HStack>
                </Flex>
              </>
            ) : null}
          </Box>

          {showPartDrawingPreview ? (
            <PartMasterfilePreview
              projectId={projectId}
              partNumber={previewPartNumber}
              title={
                previewPartNumber
                  ? `Selected part PDF · ${previewPartNumber}`
                  : 'Selected part PDF'
              }
              minH="680px"
              emptyText="Upload the part PDF named with the part number to preview it here."
            />
          ) : null}
        </Grid>
      </Box>
    </Stack>
  );
};

export default CreateWorkOrders;